/**
 * Underwriting Models — the workbook evidence explorer for this deal.
 *
 * Every underwriting workbook (.xlsx/.xlsm) uploaded to this loan lands here:
 * parsed cell-by-cell, searchable, diffed against the prior version, and
 * carried through an explicit review/promote gate. Nothing here is invented —
 * a cell only becomes a "mapped" underwriting input once a locked model
 * template anchors it (register: Model Templates), and promoting a version
 * never projects raw cells into loan calculations (see
 * backend/app/api/v1/underwriting_workbooks.py). Until that governance step
 * exists for a given model family, uploads still parse and are fully
 * browsable here — they just remain evidence rather than a "promoted" model.
 */

import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Search,
  ShieldCheck,
  XCircle,
} from 'lucide-react'

import {
  MAX_WORKBOOK_UPLOAD_BYTES,
  underwritingWorkbooksService,
  type ReviewDecisionBody,
  type UnderwritingWorkbookField,
  type UnderwritingWorkbookVersion,
  type WorkbookVersionStatus,
} from '@/services/underwritingWorkbooks'
import { DossierPanel, EmptyLine } from './ui'
import { useDossierEdit } from './editContext'
import { MODELS_NOT_AVAILABLE, useLoanWorkbooks } from './useLoanWorkbooks'

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function humanDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function errorDetail(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : fallback
}

const STATUS_STYLE: Record<WorkbookVersionStatus, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'border-stone-600 text-stone-400' },
  blocked: { label: 'Blocked', className: 'border-red-500/40 text-red-300' },
  reviewed: { label: 'Reviewed', className: 'border-sky-500/40 text-sky-300' },
  promoted: { label: 'Promoted', className: 'border-emerald-500/40 text-emerald-300' },
  superseded: { label: 'Superseded', className: 'border-stone-700 text-stone-500' },
  rejected: { label: 'Rejected', className: 'border-red-500/40 text-red-300' },
}

function StatusPill({ status }: { status: WorkbookVersionStatus }) {
  const s = STATUS_STYLE[status]
  return (
    <span className={`rounded border px-1.5 py-0.5 text-[9px] uppercase tracking-widest ${s.className}`}>
      {s.label}
    </span>
  )
}

function cellDisplay(field: UnderwritingWorkbookField): string {
  const v = field.hasCachedValue ? field.cachedValue : field.rawValue
  if (v === null || v === undefined || v === '') return field.formula ? '—' : ''
  if (typeof v === 'number') return v.toLocaleString('en-US', { maximumFractionDigits: 4 })
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE'
  return String(v)
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

// ---------------------------------------------------------------------------

function ModelBar({
  versions,
  selectedId,
  onSelect,
  onUpload,
  uploading,
  uploadError,
  unlocked,
  fill = false,
}: {
  versions: UnderwritingWorkbookVersion[]
  selectedId: string | null
  onSelect: (id: string) => void
  onUpload: (file: File) => void
  uploading: boolean
  uploadError: string | null
  unlocked: boolean
  /** A rail beside the selected version: one card per line, scrolling inside. */
  fill?: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <DossierPanel
      title="Underwriting models attached to this deal"
      action={unlocked ? 'Upload model' : undefined}
      onAction={unlocked ? () => inputRef.current?.click() : undefined}
      className={fill ? (versions.length > 0 ? 'xl:col-span-4' : 'xl:col-span-12') : ''}
      dense={fill}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xlsm"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (!file) return
          if (file.size > MAX_WORKBOOK_UPLOAD_BYTES) return
          onUpload(file)
        }}
      />
      {uploading && (
        <div className="mb-2 flex items-center gap-1.5 text-[11px] text-amber-300">
          <Loader2 size={12} className="animate-spin" /> Parsing workbook…
        </div>
      )}
      {uploadError && <p className="mb-2 text-[11px] text-red-300">{uploadError}</p>}

      {versions.length === 0 && !uploading ? (
        <EmptyLine>
          {unlocked
            ? 'No underwriting workbook has been uploaded to this deal. Upload a model (.xlsx/.xlsm) to extract and review its fields — use Unlock above if the upload control is hidden.'
            : 'No underwriting workbook has been uploaded to this deal. Unlock the dossier to upload one.'}
        </EmptyLine>
      ) : (
        <div className={`grid gap-2 sm:grid-cols-2 ${fill ? 'xl:grid-cols-1' : ''}`}>
          {versions.map((v) => {
            const on = v.id === selectedId
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => onSelect(v.id)}
                className={`rounded-md border px-3 py-2 text-left transition-colors ${
                  on
                    ? 'border-amber-300/60 bg-amber-300/[0.06]'
                    : 'border-amber-400/15 bg-black/30 hover:border-amber-400/35'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={`truncate text-[12px] font-semibold ${on ? 'text-amber-200' : 'text-stone-200'}`}>
                    v{v.versionNumber} · {v.filename}
                  </span>
                  <StatusPill status={v.status} />
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-stone-500">
                  <span>{v.sheetCount} sheets</span>
                  <span>
                    {v.mappedCellCount.toLocaleString()} / {v.importedCellCount.toLocaleString()} cells mapped
                  </span>
                  <span>{humanSize(v.sizeBytes)}</span>
                </div>
                <div className="mt-0.5 text-[9.5px] text-stone-600">
                  {v.uploadedBy} · {humanDate(v.uploadedAt)}
                </div>
                {v.blockerReasons.length > 0 && (
                  <div className="mt-1 flex items-start gap-1 text-[9.5px] text-red-300">
                    <AlertTriangle size={10} className="mt-0.5 shrink-0" />
                    <span className="truncate">{v.blockerReasons[0]}</span>
                  </div>
                )}
              </button>
            )
          })}
        </div>
      )}
    </DossierPanel>
  )
}

function FieldsExplorer({ versionId, fill = false }: { versionId: string; fill?: boolean }) {
  const [sheet, setSheet] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [mappedOnly, setMappedOnly] = useState(false)

  const fields = useQuery({
    queryKey: ['underwriting-workbook-fields', versionId],
    queryFn: () => underwritingWorkbooksService.fields(versionId),
    staleTime: 60_000,
  })

  const sheets = useMemo(() => {
    const seen = new Map<string, number>()
    for (const f of fields.data?.items ?? []) seen.set(f.sheetName, f.sheetIndex)
    return [...seen.entries()].sort((a, b) => a[1] - b[1]).map(([name]) => name)
  }, [fields.data])

  const filtered = useMemo(() => {
    const items = fields.data?.items ?? []
    const q = search.trim().toLowerCase()
    return items
      .filter((f) => (sheet ? f.sheetName === sheet : true))
      .filter((f) => (mappedOnly ? f.mapped : true))
      .filter((f) =>
        q
          ? f.label?.toLowerCase().includes(q) ||
            f.cellRef.toLowerCase().includes(q) ||
            f.semanticKey?.toLowerCase().includes(q) ||
            f.formula?.toLowerCase().includes(q)
          : true,
      )
      .sort((a, b) => a.sheetIndex - b.sheetIndex || a.rowIndex - b.rowIndex || a.columnIndex - b.columnIndex)
  }, [fields.data, sheet, mappedOnly, search])

  if (fields.isLoading) {
    return (
      <div className="flex items-center gap-1.5 py-6 text-[11px] text-stone-500">
        <Loader2 size={12} className="animate-spin" /> Loading parsed cells…
      </div>
    )
  }
  if (fields.isError) {
    return <EmptyLine>{errorDetail(fields.error, 'Could not load this workbook version.')}</EmptyLine>
  }
  const total = fields.data?.total ?? 0
  const truncatedResult = total > (fields.data?.items.length ?? 0)

  return (
    // Filling its panel at xl, the cell table takes the height the controls
    // leave and scrolls inside — one scroll, the table's — instead of a fixed
    // 420px box inside a panel that itself scrolled (a scroll inside a scroll).
    <div className={`space-y-2 ${fill ? 'xl:flex xl:min-h-0 xl:flex-1 xl:flex-col xl:gap-2 xl:space-y-0' : ''}`}>
      <div className="flex shrink-0 flex-wrap items-center gap-1.5">
        <div className="relative flex-1 min-w-[160px]">
          <Search size={11} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-stone-600" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="search label, cell, semantic key, formula…"
            className="w-full rounded border border-stone-700 bg-black/40 py-1 pl-6 pr-2 text-[11px] text-stone-200 placeholder:text-stone-600 focus:border-amber-300/50 focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => setMappedOnly((m) => !m)}
          className={`rounded border px-2 py-1 text-[10px] ${
            mappedOnly ? 'border-amber-300/50 text-amber-200' : 'border-stone-700 text-stone-400'
          }`}
        >
          mapped only
        </button>
        <select
          value={sheet ?? ''}
          onChange={(e) => setSheet(e.target.value || null)}
          className="rounded border border-stone-700 bg-black/40 px-2 py-1 text-[10.5px] text-stone-300 focus:border-amber-300/50 focus:outline-none"
        >
          <option value="">All sheets ({sheets.length})</option>
          {sheets.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {truncatedResult && (
        <p className="shrink-0 text-[9.5px] text-amber-300/80">
          Showing the first {fields.data?.items.length.toLocaleString()} of {total.toLocaleString()} cells — refine
          the sheet filter or search to narrow this down.
        </p>
      )}

      {filtered.length === 0 ? (
        <EmptyLine>No cells match this filter.</EmptyLine>
      ) : (
        <div className={`max-h-[420px] overflow-auto rounded border border-stone-800 ${fill ? 'xl:max-h-none xl:min-h-0 xl:flex-1' : ''}`}>
          <table className="w-full border-collapse text-[10.5px]">
            <thead className="sticky top-0 z-10 bg-[#141110]">
              <tr className="text-left text-[9px] uppercase tracking-wider text-stone-500">
                <th className="border-b border-stone-800 px-2 py-1">Sheet</th>
                <th className="border-b border-stone-800 px-2 py-1">Cell</th>
                <th className="border-b border-stone-800 px-2 py-1">Label</th>
                <th className="border-b border-stone-800 px-2 py-1">Formula</th>
                <th className="border-b border-stone-800 px-2 py-1 text-right">Value</th>
                <th className="border-b border-stone-800 px-2 py-1">Semantic key</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 1000).map((f) => (
                <tr key={f.fieldKey} className="hover:bg-amber-400/[0.04]">
                  <td className="truncate border-b border-stone-900 px-2 py-1 text-stone-500">{f.sheetName}</td>
                  <td className="whitespace-nowrap border-b border-stone-900 px-2 py-1 font-mono text-stone-400">
                    {f.cellRef}
                  </td>
                  <td className="max-w-[220px] truncate border-b border-stone-900 px-2 py-1 text-stone-300" title={f.label ?? ''}>
                    {f.label || '—'}
                  </td>
                  <td
                    className="max-w-[200px] truncate border-b border-stone-900 px-2 py-1 font-mono text-amber-200/70"
                    title={f.formula ?? ''}
                  >
                    {f.formula || ''}
                  </td>
                  <td className="whitespace-nowrap border-b border-stone-900 px-2 py-1 text-right font-mono text-stone-200">
                    {cellDisplay(f)}
                  </td>
                  <td className="border-b border-stone-900 px-2 py-1">
                    {f.semanticKey ? (
                      <span className="rounded border border-emerald-500/30 px-1 text-[9px] text-emerald-300">
                        {f.semanticKey}
                      </span>
                    ) : (
                      <span className="text-stone-700">unmapped</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function ChangesList({ versionId, fill = false }: { versionId: string; fill?: boolean }) {
  const diff = useQuery({
    queryKey: ['underwriting-workbook-diff', versionId],
    queryFn: () => underwritingWorkbooksService.diff(versionId),
    staleTime: 60_000,
  })
  if (diff.isLoading) {
    return (
      <div className="flex items-center gap-1.5 py-4 text-[11px] text-stone-500">
        <Loader2 size={12} className="animate-spin" /> Loading changes…
      </div>
    )
  }
  if (!diff.data?.previousVersionId) {
    return <EmptyLine>This is the first version — nothing to compare against.</EmptyLine>
  }
  if (diff.data.items.length === 0) {
    return <EmptyLine>No cell-level changes from the prior version.</EmptyLine>
  }
  return (
    <div className={`max-h-[360px] space-y-1 overflow-auto ${fill ? 'xl:max-h-none xl:min-h-0 xl:flex-1' : ''}`}>
      {diff.data.items.slice(0, 500).map((c) => (
        <div key={c.comparisonKey} className="rounded border border-stone-800 px-2 py-1.5 text-[10.5px]">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-stone-300">{c.fieldKeyAfter ?? c.fieldKeyBefore}</span>
            <div className="flex gap-1">
              {c.changeTypes.map((t) => (
                <span key={t} className="rounded border border-stone-700 px-1 text-[8.5px] uppercase text-stone-500">
                  {t}
                </span>
              ))}
            </div>
          </div>
          {c.changeTypes.includes('value') && (
            <div className="mt-0.5 text-stone-500">
              value: <span className="text-red-300/80">{String(c.valueBefore ?? '—')}</span> →{' '}
              <span className="text-emerald-300/80">{String(c.valueAfter ?? '—')}</span>
            </div>
          )}
          {c.changeTypes.includes('formula') && (
            <div className="mt-0.5 truncate font-mono text-stone-600">
              {c.formulaBefore ?? '—'} → {c.formulaAfter ?? '—'}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function ReviewPromotePanel({
  version,
  unlocked,
}: {
  version: UnderwritingWorkbookVersion
  unlocked: boolean
}) {
  const queryClient = useQueryClient()
  const [note, setNote] = useState('')
  const [mappingBasis, setMappingBasis] = useState('')
  const [calcBasis, setCalcBasis] = useState('')
  const [confirmMapping, setConfirmMapping] = useState(false)
  const [confirmCalc, setConfirmCalc] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['loan-underwriting-workbooks'] })

  const review = useMutation({
    mutationFn: (body: ReviewDecisionBody) => underwritingWorkbooksService.review(version.id, body),
    onSuccess: () => {
      setActionError(null)
      invalidate()
    },
    onError: (e) => setActionError(errorDetail(e, 'That review action was rejected.')),
  })

  const promote = useMutation({
    mutationFn: () => underwritingWorkbooksService.promote(version.id),
    onSuccess: () => {
      setActionError(null)
      invalidate()
    },
    onError: (e) => setActionError(errorDetail(e, 'That promotion was rejected.')),
  })

  const canReview = version.status === 'draft' || version.status === 'blocked'
  const hasTemplate = Boolean(version.templateId)
  // A formula the sponsor saved without a cached result is a cell with no
  // number in it — not a reason to withhold the thousands of figures beside
  // it. The sponsor's workbook is a set of claims the sponsor must stand
  // behind; the desk underwrites independently and does not rely on the
  // sponsor's arithmetic. It is stated below and on each affected cell, and it
  // no longer gates review.
  const parseOk = version.parseStatus === 'complete' && !version.truncated

  const gateNotes: string[] = []
  if (!parseOk) gateNotes.push('The parse is blocked or truncated.')
  if (version.missingCachedFormulaCount > 0) {
    gateNotes.push(
      `${version.missingCachedFormulaCount} formula cells were saved without a cached result and carry no value. Every other cell is imported as the sponsor stated it.`,
    )
  }
  if (!hasTemplate) {
    gateNotes.push(
      'This upload has no locked model template attached, so it is evidence-only — it can be browsed here but not reviewed or promoted until a template governs it (register: Model Templates).',
    )
  } else if (version.mappedCellCount === 0) {
    gateNotes.push('The attached template mapped zero cells in this workbook.')
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-[10.5px] text-stone-400">
        <span className="flex items-center gap-1">
          {version.mappingReviewed ? (
            <CheckCircle2 size={12} className="text-emerald-400" />
          ) : (
            <XCircle size={12} className="text-stone-600" />
          )}
          Client mapping confirmed
        </span>
        <span className="flex items-center gap-1">
          {version.calculationSignedOff ? (
            <CheckCircle2 size={12} className="text-emerald-400" />
          ) : (
            <XCircle size={12} className="text-stone-600" />
          )}
          Calculation sign-off
        </span>
        <span className="flex items-center gap-1">
          <ShieldCheck size={12} className={hasTemplate ? 'text-emerald-400' : 'text-stone-600'} />
          {hasTemplate ? 'Governed by a locked template' : 'No governing template'}
        </span>
      </div>

      {gateNotes.length > 0 && (
        <div className="space-y-1 rounded border border-amber-400/20 bg-amber-400/[0.05] px-2.5 py-2 text-[10.5px] text-amber-100/90">
          {gateNotes.map((n) => (
            <p key={n}>{n}</p>
          ))}
        </div>
      )}

      {!unlocked && (
        <p className="text-[10.5px] text-stone-500">Unlock the dossier above to review or promote this version.</p>
      )}

      {unlocked && canReview && hasTemplate && parseOk && version.mappedCellCount > 0 && (
        // At xl each confirmation sits beside its basis, two to a row, so the
        // form fits the Workbook Evidence screen at 1366x768 without a scroll.
        <div className="space-y-2 rounded border border-stone-800 px-2.5 py-2 xl:grid xl:grid-cols-2 xl:items-center xl:gap-x-3 xl:gap-y-2 xl:space-y-0">
          <label className="flex items-center gap-1.5 text-[10.5px] text-stone-300">
            <input type="checkbox" checked={confirmMapping} onChange={(e) => setConfirmMapping(e.target.checked)} />
            I confirm the client model map for this workbook
          </label>
          <input
            value={mappingBasis}
            onChange={(e) => setMappingBasis(e.target.value)}
            placeholder="mapping basis — how was this confirmed?"
            className="w-full rounded border border-stone-700 bg-black/40 px-2 py-1 text-[10.5px] text-stone-200 placeholder:text-stone-600"
          />
          <label className="flex items-center gap-1.5 text-[10.5px] text-stone-300">
            <input type="checkbox" checked={confirmCalc} onChange={(e) => setConfirmCalc(e.target.checked)} />
            I sign off on the calculations in this workbook
          </label>
          <input
            value={calcBasis}
            onChange={(e) => setCalcBasis(e.target.value)}
            placeholder="calculation basis — how was this verified?"
            className="w-full rounded border border-stone-700 bg-black/40 px-2 py-1 text-[10.5px] text-stone-200 placeholder:text-stone-600"
          />
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="review note"
            className="w-full rounded border border-stone-700 bg-black/40 px-2 py-1 text-[10.5px] text-stone-200 placeholder:text-stone-600"
          />
          <div className="flex gap-2">
            <button
              type="button"
              disabled={
                review.isPending || !note.trim() || !confirmMapping || !confirmCalc || !mappingBasis.trim() || !calcBasis.trim()
              }
              onClick={() =>
                review.mutate({
                  decision: 'reviewed',
                  note,
                  confirmClientMapping: confirmMapping,
                  confirmCalculationSignoff: confirmCalc,
                  mappingBasis,
                  calculationBasis: calcBasis,
                })
              }
              className="rounded bg-amber-400/90 px-2.5 py-1 text-[10.5px] font-medium text-black disabled:opacity-40"
            >
              {review.isPending ? 'Marking reviewed…' : 'Mark reviewed'}
            </button>
            <button
              type="button"
              disabled={review.isPending || !note.trim()}
              onClick={() => review.mutate({ decision: 'rejected', note })}
              className="rounded border border-red-500/40 px-2.5 py-1 text-[10.5px] text-red-300 disabled:opacity-40"
            >
              Reject
            </button>
          </div>
        </div>
      )}

      {unlocked && version.status === 'reviewed' && (
        <button
          type="button"
          disabled={promote.isPending}
          onClick={() => promote.mutate()}
          className="rounded border border-emerald-500/40 px-2.5 py-1 text-[10.5px] text-emerald-300 disabled:opacity-40"
        >
          {promote.isPending ? 'Promoting…' : 'Promote this version'}
        </button>
      )}

      {actionError && <p className="text-[10.5px] text-red-300">{actionError}</p>}

      <div className="flex gap-3 border-t border-stone-800 pt-2 text-[10px] text-stone-500">
        <button
          type="button"
          onClick={async () => {
            const blob = await underwritingWorkbooksService.downloadExport(version.id)
            downloadBlob(blob, `${version.filename}.fields.csv`)
          }}
          className="flex items-center gap-1 hover:text-amber-300"
        >
          <Download size={11} /> Export fields (CSV)
        </button>
        <button
          type="button"
          onClick={async () => {
            const blob = await underwritingWorkbooksService.downloadSource(version.id)
            downloadBlob(blob, version.filename)
          }}
          className="flex items-center gap-1 hover:text-amber-300"
        >
          <Download size={11} /> Download source workbook
        </button>
      </div>
    </div>
  )
}

const SUBTABS = [
  { id: 'fields', label: 'Field map' },
  { id: 'changes', label: 'Changes' },
  { id: 'review', label: 'Review & promote' },
] as const
type SubtabId = (typeof SUBTABS)[number]['id']

function VersionDetail({
  version,
  unlocked,
  fill = false,
}: {
  version: UnderwritingWorkbookVersion
  unlocked: boolean
  /** Fill its grid cell: the field map's table, or the change list, takes
   *  the height left under the tabs and scrolls inside. */
  fill?: boolean
}) {
  const [subtab, setSubtab] = useState<SubtabId>('fields')
  return (
    <DossierPanel title={`v${version.versionNumber} · ${version.filename}`} className={fill ? 'xl:col-span-8' : ''} dense={fill}>
      <div className="mb-2 flex shrink-0 flex-wrap items-center gap-3 text-[10.5px] text-stone-500">
        <StatusPill status={version.status} />
        <span>{version.sheetCount} sheets</span>
        <span>{version.formulaCellCount.toLocaleString()} formulas</span>
        {version.missingCachedFormulaCount > 0 && (
          <span className="text-amber-300">
            {version.missingCachedFormulaCount} formula cells carry no value
          </span>
        )}
        {version.diffSummary?.available ? (
          <span>vs v{(version.diffSummary as { previous_version_id?: string }).previous_version_id ? 'prior' : ''}: changes tracked</span>
        ) : null}
      </div>
      <div className="mb-2 flex shrink-0 gap-1 border-b border-stone-800">
        {SUBTABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setSubtab(t.id)}
            className={`px-2 py-1 text-[10.5px] uppercase tracking-wider ${
              subtab === t.id ? 'border-b-2 border-amber-300 text-amber-200' : 'text-stone-500 hover:text-stone-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {subtab === 'fields' && <FieldsExplorer versionId={version.id} fill={fill} />}
      {subtab === 'changes' && <ChangesList versionId={version.id} fill={fill} />}
      {subtab === 'review' && <ReviewPromotePanel version={version} unlocked={unlocked} />}
    </DossierPanel>
  )
}

/**
 * `fill`: lay out to the screen it is given (Underwriting > Model Review >
 * Workbook Evidence, under the one-screen rule). The models attached to the
 * deal become a rail on the left and the selected version takes the rest, at
 * the same height, its cell table scrolling inside. Stacked, the two panels ran
 * 719px through a 309px slot at 1366x768 and the 420px table inside them
 * scrolled again.
 */
export function UnderwritingWorkbookPanel({ loanId, fill = false }: { loanId: string; fill?: boolean }) {
  const { unlocked } = useDossierEdit()
  const queryClient = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const { query: versions, unavailable } = useLoanWorkbooks(loanId)

  const upload = useMutation({
    mutationFn: (file: File) => underwritingWorkbooksService.upload(loanId, file),
    onSuccess: (version) => {
      setUploadError(null)
      setSelectedId(version.id)
      // Every workbook list, not just this deal's: a property's model is part of its
      // portfolio's section and roll-up, which are not read again on every visit.
      queryClient.invalidateQueries({ queryKey: ['loan-underwriting-workbooks'] })
    },
    onError: (e) => setUploadError(errorDetail(e, 'That workbook could not be uploaded.')),
  })

  const list = versions.data ?? []
  const selected =
    list.find((v) => v.id === selectedId) ??
    list.find((v) => v.status === 'promoted') ??
    list[0] ??
    null

  if (unavailable) {
    return <p className="py-4 text-[11px] text-stone-500">{MODELS_NOT_AVAILABLE}</p>
  }

  return (
    <div className={fill ? 'grid grid-cols-1 gap-2 xl:h-full xl:min-h-0 xl:grid-cols-12' : 'space-y-3'}>
      {versions.isLoading && (
        <div className="flex items-center gap-1.5 py-4 text-[11px] text-stone-500 xl:col-span-12">
          <Loader2 size={12} className="animate-spin" /> Loading underwriting models…
        </div>
      )}
      {versions.isError && (
        <p className="text-[11px] text-red-300 xl:col-span-12">{errorDetail(versions.error, 'Could not load underwriting models for this deal.')}</p>
      )}
      {!versions.isLoading && !versions.isError && (
        <>
          <ModelBar
            versions={list}
            selectedId={selected?.id ?? null}
            onSelect={setSelectedId}
            onUpload={(f) => upload.mutate(f)}
            uploading={upload.isPending}
            uploadError={uploadError}
            unlocked={unlocked}
            fill={fill}
          />
          {selected ? (
            <VersionDetail version={selected} unlocked={unlocked} fill={fill} />
          ) : (
            // Filling the screen, the empty rail spans it and says this itself.
            list.length === 0 && !fill && (
              <div className="flex items-center gap-1.5 text-[10.5px] text-stone-600">
                <FileSpreadsheet size={12} /> Upload a model above to extract and review it here.
              </div>
            )
          )}
        </>
      )}
    </div>
  )
}
